import { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { API_V1, authFetch, formatAmount } from '../constants';
import PageLayout from '../components/ui/PageLayout';
import KpiGrid from '../components/ui/KpiGrid';
import DataTable, { Column } from '../components/ui/DataTable';
import ActionButton from '../components/ui/ActionButton';
import StatusBadge from '../components/ui/StatusBadge';
import { useEntities } from '../hooks/useEntities';

const API = `${API_V1}/contracts`;

interface Extraction {
  counterparty_name?: string;
  counterparty_type?: string;
  contract_type?: string;
  title?: string;
  effective_date?: string;
  expiration_date?: string;
  total_value?: number;
  currency?: string;
  payment_terms?: string;
  auto_renewal?: boolean;
  line_items?: { item_code?: string; description?: string; unit_price?: number; quantity?: number }[];
  obligations?: { type?: string; description?: string; due_date?: string; amount?: number }[];
  billing_schedule?: { period?: string; amount?: number; due_date?: string; description?: string }[];
  flagged_clauses?: { clause_text?: string; issue?: string; severity?: string; recommendation?: string }[];
  risk_score?: number;
  confidence?: number;
  summary?: string;
  clauses?: Record<string, any>;
}

interface EntitySuggestion {
  matched_entity_code?: string | null;
  matched_entity_name?: string | null;
  matched_party_name?: string | null;
  matched_party?: string | null;
  match_method?: string;
  match_confidence?: 'HIGH' | 'MEDIUM' | 'LOW' | 'NONE';
  party_a_name?: string;
  party_b_name?: string;
  available_entities?: {
    entity_code: string;
    entity_name: string;
    functional_currency?: string;
    country?: string;
  }[];
}

interface ExtractResult {
  success: boolean;
  method?: string;
  confidence?: number;
  risk_score?: number;
  flagged_count?: number;
  summary?: string;
  extracted?: Extraction;
  filename?: string;
  entity_suggestion?: EntitySuggestion;
}

type Step = 'upload' | 'review' | 'created';

export default function ContractIntelligence() {
  const navigate = useNavigate();
  const fileRef = useRef<HTMLInputElement>(null);
  const registeredEntities = useEntities();
  const [step, setStep] = useState<Step>('upload');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [extraction, setExtraction] = useState<ExtractResult | null>(null);
  const [createdResult, setCreatedResult] = useState<any>(null);
  const [contractNumber, setContractNumber] = useState('');
  const [selectedEntity, setSelectedEntity] = useState<string>('');
  const [dragOver, setDragOver] = useState(false);
  const [textMode, setTextMode] = useState(false);
  const [contractText, setContractText] = useState('');
  // Counterparty enrichment — fields not in the contract that user must provide
  const [counterparty, setCounterparty] = useState({
    tax_id: '', credit_limit: '', bank_name: '', bank_account: '', bank_routing: '',
    contact_email: '', contact_phone: '', address_line1: '', address_city: '', address_country: '',
    customer_type: '', vendor_type: '',
  });

  const handleFile = async (file: File) => {
    setLoading(true);
    setError('');
    try {
      const base64 = await fileToBase64(file);
      let mediaType = 'application/pdf';
      if (file.type.startsWith('image/')) mediaType = file.type;

      const res = await authFetch(`${API}/extract-document`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ file_base64: base64, media_type: mediaType, filename: file.name }),
      });
      if (res.ok) {
        const data: ExtractResult = await res.json();
        setExtraction(data);
        // Pre-select the AI-matched entity (user can override before Create)
        setSelectedEntity(data.entity_suggestion?.matched_entity_code || '');
        setStep('review');
      } else {
        const e = await res.json();
        setError(e.detail || 'Extraction failed');
      }
    } catch (e: any) {
      setError(e.message || 'Upload failed');
    }
    setLoading(false);
  };

  const handleTextExtract = async () => {
    if (contractText.length < 50) { setError('Paste at least 50 characters of contract text'); return; }
    setLoading(true);
    setError('');
    try {
      const res = await authFetch(`${API}/extract-text`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ contract_text: contractText }),
      });
      if (res.ok) {
        const data: ExtractResult = await res.json();
        setExtraction(data);
        setSelectedEntity(data.entity_suggestion?.matched_entity_code || '');
        setStep('review');
      } else {
        const e = await res.json();
        setError(e.detail || 'Extraction failed');
      }
    } catch (e: any) {
      setError(e.message || 'Extraction failed');
    }
    setLoading(false);
  };

  const handleCreate = async () => {
    if (!extraction?.extracted) return;
    // Fail-fast: entity_code is required
    if (!selectedEntity) {
      setError('Legal entity is required. Please select the entity that owns this contract before creating.');
      return;
    }
    setLoading(true);
    setError('');
    try {
      // Merge counterparty enrichment data into extraction
      const enrichedExtraction = {
        ...extraction.extracted,
        counterparty_enrichment: {
          tax_id: counterparty.tax_id || undefined,
          credit_limit: counterparty.credit_limit ? parseFloat(counterparty.credit_limit) : undefined,
          bank_name: counterparty.bank_name || undefined,
          bank_account: counterparty.bank_account || undefined,
          bank_routing: counterparty.bank_routing || undefined,
          contact_email: counterparty.contact_email || undefined,
          contact_phone: counterparty.contact_phone || undefined,
          address_line1: counterparty.address_line1 || undefined,
          address_city: counterparty.address_city || undefined,
          address_country: counterparty.address_country || undefined,
          customer_type: counterparty.customer_type || undefined,
          vendor_type: counterparty.vendor_type || undefined,
        },
      };
      const res = await authFetch(`${API}/create-from-extraction`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          extracted: enrichedExtraction,
          contract_number: contractNumber || undefined,
          entity_code: selectedEntity,
        }),
      });
      if (res.ok) {
        const result = await res.json();
        setCreatedResult(result);
        setStep('created');
      } else {
        const e = await res.json();
        setError(e.detail || 'Creation failed');
      }
    } catch (e: any) {
      setError(e.message || 'Creation failed');
    }
    setLoading(false);
  };

  const reset = () => { setStep('upload'); setExtraction(null); setCreatedResult(null); setError(''); setContractNumber(''); setContractText(''); setSelectedEntity(''); };

  const ext = extraction?.extracted;
  const inputStyle: React.CSSProperties = { padding: '8px 12px', border: '1px solid var(--border-default)', borderRadius: 6, fontSize: 12, width: '100%', background: 'var(--bg-card)', color: 'var(--text-primary)' };

  const riskColor = (score: number) => score >= 60 ? 'var(--negative)' : score >= 30 ? 'var(--warning)' : 'var(--positive)';
  const severityColor = (s: string) => s === 'HIGH' ? 'var(--negative)' : s === 'MEDIUM' ? 'var(--warning)' : 'var(--text-secondary)';

  const lineItemCols: Column[] = [
    { key: 'item_code', label: 'Item', render: (v: string) => <span style={{ fontWeight: 600 }}>{v || '-'}</span> },
    { key: 'description', label: 'Description' },
    { key: 'unit_price', label: 'Unit Price', align: 'right', render: (v: number | null) => v != null ? `$${v.toLocaleString()}` : '-' },
    { key: 'quantity', label: 'Qty', align: 'right' },
  ];

  const obligationCols: Column[] = [
    { key: 'type', label: 'Type', render: (v: string) => <span style={{ fontWeight: 600 }}>{v}</span> },
    { key: 'description', label: 'Description' },
    { key: 'due_date', label: 'Due Date' },
    { key: 'amount', label: 'Amount', align: 'right', render: (v: number | null) => v != null ? formatAmount(v) : '-' },
  ];

  const billingCols: Column[] = [
    { key: 'period', label: 'Period', render: (v: string) => <span style={{ fontWeight: 600 }}>{v}</span> },
    { key: 'description', label: 'Description' },
    { key: 'due_date', label: 'Due Date' },
    { key: 'amount', label: 'Amount', align: 'right', render: (v: number | null) => v != null ? formatAmount(v) : '-' },
  ];

  return (
    <PageLayout
      title="Contract Intelligence"
      subtitle="Upload a contract document -- AI extracts all fields, flags risks, auto-creates linked records"
      action={{ label: 'Back to Contracts', onClick: () => navigate('/contracts'), variant: 'secondary' }}
    >
      {/* Step indicator */}
      <div style={{ display: 'flex', gap: 8, marginBottom: 24 }}>
        {[{ key: 'upload', label: '1. Upload' }, { key: 'review', label: '2. Review Extraction' }, { key: 'created', label: '3. Contract Created' }].map(s => (
          <div key={s.key} style={{ flex: 1, textAlign: 'center', padding: '8px', borderRadius: 6, fontSize: 12, fontWeight: 600, background: step === s.key ? 'var(--accent)' : 'var(--bg-card)', color: step === s.key ? '#fff' : 'var(--text-secondary)', border: '1px solid var(--border-subtle)' }}>{s.label}</div>
        ))}
      </div>

      {error && <div style={{ padding: '10px 16px', borderRadius: 6, background: 'var(--negative-bg)', color: 'var(--negative)', fontSize: 12, marginBottom: 16 }}>{error}</div>}

      {/* Step 1: Upload */}
      {step === 'upload' && (
        <div>
          <div style={{ display: 'flex', gap: 8, marginBottom: 16 }}>
            <ActionButton label="Upload Document" onClick={() => { setTextMode(false); fileRef.current?.click(); }} variant={textMode ? 'secondary' : 'primary'} size="sm" />
            <ActionButton label="Paste Text" onClick={() => setTextMode(true)} variant={!textMode ? 'secondary' : 'primary'} size="sm" />
          </div>

          {!textMode ? (
            <div
              onClick={() => fileRef.current?.click()}
              onDragOver={e => { e.preventDefault(); setDragOver(true); }}
              onDragLeave={() => setDragOver(false)}
              onDrop={e => { e.preventDefault(); setDragOver(false); const f = e.dataTransfer.files[0]; if (f) handleFile(f); }}
              style={{
                border: `2px dashed ${dragOver ? 'var(--accent)' : 'var(--border-default)'}`,
                borderRadius: 12, padding: '60px 40px', textAlign: 'center', cursor: 'pointer',
                background: dragOver ? 'var(--accent-subtle, #eef2ff)' : 'var(--bg-card)',
                transition: 'all 0.2s',
              }}
            >
              <div style={{ fontSize: 48, marginBottom: 12 }}>&#128196;</div>
              <div style={{ fontSize: 16, fontWeight: 600, marginBottom: 6, color: 'var(--text-primary)' }}>
                {loading ? 'Analyzing contract...' : 'Drop contract PDF or image here'}
              </div>
              <div style={{ fontSize: 12, color: 'var(--text-secondary)' }}>
                {loading ? 'Claude Vision is extracting all fields, obligations, and clauses' : 'Supports PDF, PNG, JPG -- or click to browse'}
              </div>
              {loading && <div style={{ marginTop: 16, height: 4, borderRadius: 2, background: 'var(--border-subtle)', overflow: 'hidden' }}><div style={{ width: '60%', height: '100%', background: 'var(--accent)', animation: 'pulse 1.5s infinite' }} /></div>}
              <input ref={fileRef} type="file" accept=".pdf,.png,.jpg,.jpeg" style={{ display: 'none' }} onChange={e => { const f = e.target.files?.[0]; if (f) handleFile(f); }} />
            </div>
          ) : (
            <div>
              <textarea
                value={contractText}
                onChange={e => setContractText(e.target.value)}
                placeholder="Paste the full contract text here (minimum 50 characters)..."
                style={{ ...inputStyle, minHeight: 250, fontFamily: 'monospace', resize: 'vertical' }}
              />
              <div style={{ marginTop: 12 }}>
                <ActionButton label={loading ? 'Analyzing...' : 'Extract Contract Fields'} onClick={handleTextExtract} disabled={loading || contractText.length < 50} />
              </div>
            </div>
          )}
        </div>
      )}

      {/* Step 2: Review Extraction */}
      {step === 'review' && ext && (
        <div>
          {/* ─── LEGAL ENTITY SELECTOR — REQUIRED BEFORE CREATE ─── */}
          {(() => {
            const es = extraction?.entity_suggestion;
            const confidence = es?.match_confidence || 'NONE';
            const bannerBg =
              confidence === 'HIGH' ? '#f0fdf4' :
              confidence === 'MEDIUM' ? '#fefce8' :
              confidence === 'LOW' ? '#fff7ed' : '#fef2f2';
            const bannerBorder =
              confidence === 'HIGH' ? '#22c55e' :
              confidence === 'MEDIUM' ? '#eab308' :
              confidence === 'LOW' ? '#f97316' : '#ef4444';
            const bannerText =
              confidence === 'HIGH'
                ? `✓ AI matched legal entity with HIGH confidence: ${es?.matched_entity_code} — ${es?.matched_entity_name} (via ${es?.match_method} match on "${es?.matched_party_name}"). Confirm or override below.`
                : confidence === 'MEDIUM'
                ? `⚠ AI matched with MEDIUM confidence: ${es?.matched_entity_code} — ${es?.matched_entity_name} (via ${es?.match_method}). Please confirm.`
                : confidence === 'LOW'
                ? `⚠ AI matched with LOW confidence: ${es?.matched_entity_code} — ${es?.matched_entity_name} (fuzzy root-word match). Please verify.`
                : `❌ AI could not match any of our registered legal entities. Parties found: "${es?.party_a_name || '?'}" and "${es?.party_b_name || '?'}". Please select manually.`;

            return (
              <div style={{
                padding: 14, marginBottom: 16, borderRadius: 8,
                background: bannerBg, border: `1px solid ${bannerBorder}`,
              }}>
                <div style={{ fontSize: 12, fontWeight: 600, marginBottom: 10, color: 'var(--text-primary)' }}>
                  {bannerText}
                </div>
                <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
                  <label style={{ fontSize: 11, fontWeight: 700, color: 'var(--text-secondary)', textTransform: 'uppercase', minWidth: 100 }}>
                    Legal Entity *
                  </label>
                  <select
                    aria-label="Legal Entity"
                    value={selectedEntity}
                    onChange={e => setSelectedEntity(e.target.value)}
                    style={{ ...inputStyle, maxWidth: 500 }}
                  >
                    <option value="">-- Select legal entity --</option>
                    {((es?.available_entities && es.available_entities.length > 0)
                      ? es.available_entities
                      : registeredEntities
                    ).map((ent: any) => (
                      <option key={ent.entity_code} value={ent.entity_code}>
                        {ent.entity_code} — {ent.entity_name}{ent.functional_currency ? ` (${ent.functional_currency})` : ''}
                      </option>
                    ))}
                  </select>
                  {selectedEntity && es?.matched_entity_code && selectedEntity !== es.matched_entity_code && (
                    <span style={{ fontSize: 11, color: 'var(--warning)', fontWeight: 600 }}>
                      (overriding AI match)
                    </span>
                  )}
                </div>
                <div style={{ fontSize: 10, color: 'var(--text-hint)', marginTop: 8, fontStyle: 'italic' }}>
                  This entity will be linked to the contract and all downstream records (insurance, debt, equity, vendor, customer, revenue).
                  Required — the contract cannot be created without a legal entity.
                </div>
              </div>
            );
          })()}

          <KpiGrid columns={6} items={[
            { label: 'Risk Score', value: String(ext.risk_score ?? 0), color: riskColor(ext.risk_score ?? 0) },
            { label: 'Confidence', value: `${Math.round((ext.confidence ?? 0) * 100)}%`, color: 'var(--accent)' },
            { label: 'Flagged Clauses', value: String(extraction?.flagged_count ?? 0), color: (extraction?.flagged_count ?? 0) > 0 ? 'var(--negative)' : 'var(--positive)' },
            { label: 'Line Items', value: String(ext.line_items?.length ?? 0) },
            { label: 'Obligations', value: String(ext.obligations?.length ?? 0) },
            { label: 'Billing Entries', value: String(ext.billing_schedule?.length ?? 0) },
          ]} />

          {/* Summary */}
          {ext.summary && <div style={{ padding: '12px 16px', background: 'var(--bg-card)', border: '1px solid var(--border-subtle)', borderRadius: 8, marginBottom: 16, fontSize: 13, lineHeight: 1.5, color: 'var(--text-primary)' }}>{ext.summary}</div>}

          {/* Contract details grid */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 20 }}>
            <Section title="Contract Details">
              <Field label="Counterparty" value={ext.counterparty_name} />
              <Field label="Type" value={ext.contract_type} />
              <Field label="Title" value={ext.title} />
              <Field label="Value" value={ext.total_value != null ? formatAmount(ext.total_value) : '-'} />
              <Field label="Currency" value={ext.currency} />
              <Field label="Payment Terms" value={ext.payment_terms} />
              <Field label="Effective Date" value={ext.effective_date} />
              <Field label="Expiration Date" value={ext.expiration_date} />
              <Field label="Auto-Renewal" value={ext.auto_renewal ? 'Yes' : 'No'} />
            </Section>

            <Section title="Key Clauses">
              {ext.clauses && Object.entries(ext.clauses).map(([k, v]) => (
                <Field key={k} label={k.replace(/_/g, ' ')} value={String(v ?? '-')} />
              ))}
            </Section>
          </div>

          {/* Flagged clauses */}
          {ext.flagged_clauses && ext.flagged_clauses.length > 0 && (
            <div style={{ marginBottom: 20, border: '1px solid var(--negative)', borderRadius: 8, overflow: 'hidden' }}>
              <div style={{ padding: '8px 14px', background: 'var(--negative-bg)', fontWeight: 700, fontSize: 11, color: 'var(--negative)', textTransform: 'uppercase' as const }}>Flagged Clauses ({ext.flagged_clauses.length})</div>
              {ext.flagged_clauses.map((fc, i) => (
                <div key={i} style={{ padding: '10px 14px', borderTop: i > 0 ? '1px solid var(--border-subtle)' : 'none', background: 'var(--bg-card)' }}>
                  <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 4 }}>
                    <span style={{ fontSize: 10, fontWeight: 700, padding: '2px 6px', borderRadius: 4, background: severityColor(fc.severity || 'LOW'), color: '#fff' }}>{fc.severity}</span>
                    <span style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-primary)' }}>{fc.issue}</span>
                  </div>
                  <div style={{ fontSize: 11, color: 'var(--text-secondary)', fontStyle: 'italic', marginBottom: 4 }}>"{fc.clause_text}"</div>
                  <div style={{ fontSize: 11, color: 'var(--accent)' }}>{fc.recommendation}</div>
                </div>
              ))}
            </div>
          )}

          {/* Line items */}
          {ext.line_items && ext.line_items.length > 0 && (
            <div style={{ marginBottom: 20 }}>
              <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--text-secondary)', marginBottom: 6, textTransform: 'uppercase' as const }}>Price List ({ext.line_items.length} items)</div>
              <DataTable columns={lineItemCols} rows={ext.line_items} />
            </div>
          )}

          {/* Obligations */}
          {ext.obligations && ext.obligations.length > 0 && (
            <div style={{ marginBottom: 20 }}>
              <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--text-secondary)', marginBottom: 6, textTransform: 'uppercase' as const }}>Obligations ({ext.obligations.length})</div>
              <DataTable columns={obligationCols} rows={ext.obligations} />
            </div>
          )}

          {/* Billing schedule */}
          {ext.billing_schedule && ext.billing_schedule.length > 0 && (
            <div style={{ marginBottom: 20 }}>
              <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--text-secondary)', marginBottom: 6, textTransform: 'uppercase' as const }}>Billing Schedule ({ext.billing_schedule.length} entries)</div>
              <DataTable columns={billingCols} rows={ext.billing_schedule} />
            </div>
          )}

          {/* Counterparty Enrichment */}
          <div style={{ marginBottom: 20, padding: '16px', background: 'var(--bg-card)', borderRadius: 8, border: '2px solid var(--accent)' }}>
            <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--accent)', marginBottom: 4, textTransform: 'uppercase' as const }}>
              Counterparty Details -- Complete Missing Information
            </div>
            <div style={{ fontSize: 11, color: 'var(--text-secondary)', marginBottom: 12 }}>
              These fields are NOT in the contract. Fill them now to create a complete {ext?.counterparty_type === 'CUSTOMER' ? 'customer' : 'vendor'} record.
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 10 }}>
              <div>
                <label style={{ fontSize: 10, color: 'var(--text-secondary)', fontWeight: 600 }}>Tax ID *</label>
                <input placeholder="e.g. 12-3456789" value={counterparty.tax_id} onChange={e => setCounterparty({ ...counterparty, tax_id: e.target.value })} style={inputStyle} />
              </div>
              {ext?.counterparty_type === 'CUSTOMER' && (
                <div>
                  <label style={{ fontSize: 10, color: 'var(--text-secondary)', fontWeight: 600 }}>Credit Limit ($)</label>
                  <input placeholder="e.g. 500000" type="number" value={counterparty.credit_limit} onChange={e => setCounterparty({ ...counterparty, credit_limit: e.target.value })} style={inputStyle} />
                </div>
              )}
              <div>
                <label style={{ fontSize: 10, color: 'var(--text-secondary)', fontWeight: 600 }}>{ext?.counterparty_type === 'CUSTOMER' ? 'Customer' : 'Vendor'} Type</label>
                <input placeholder="e.g. ENTERPRISE, SMB" value={ext?.counterparty_type === 'CUSTOMER' ? counterparty.customer_type : counterparty.vendor_type} onChange={e => setCounterparty({ ...counterparty, [ext?.counterparty_type === 'CUSTOMER' ? 'customer_type' : 'vendor_type']: e.target.value })} style={inputStyle} />
              </div>
              <div>
                <label style={{ fontSize: 10, color: 'var(--text-secondary)', fontWeight: 600 }}>Contact Email</label>
                <input placeholder="ap@company.com" type="email" value={counterparty.contact_email} onChange={e => setCounterparty({ ...counterparty, contact_email: e.target.value })} style={inputStyle} />
              </div>
              <div>
                <label style={{ fontSize: 10, color: 'var(--text-secondary)', fontWeight: 600 }}>Contact Phone</label>
                <input placeholder="+1 555-0100" value={counterparty.contact_phone} onChange={e => setCounterparty({ ...counterparty, contact_phone: e.target.value })} style={inputStyle} />
              </div>
              <div>
                <label style={{ fontSize: 10, color: 'var(--text-secondary)', fontWeight: 600 }}>Country</label>
                <input placeholder="US, SA, BR" value={counterparty.address_country} onChange={e => setCounterparty({ ...counterparty, address_country: e.target.value })} style={inputStyle} />
              </div>
              <div>
                <label style={{ fontSize: 10, color: 'var(--text-secondary)', fontWeight: 600 }}>Bank Name</label>
                <input placeholder="Chase, HSBC" value={counterparty.bank_name} onChange={e => setCounterparty({ ...counterparty, bank_name: e.target.value })} style={inputStyle} />
              </div>
              <div>
                <label style={{ fontSize: 10, color: 'var(--text-secondary)', fontWeight: 600 }}>Bank Account #</label>
                <input placeholder="Account number" value={counterparty.bank_account} onChange={e => setCounterparty({ ...counterparty, bank_account: e.target.value })} style={inputStyle} />
              </div>
              <div>
                <label style={{ fontSize: 10, color: 'var(--text-secondary)', fontWeight: 600 }}>Routing #</label>
                <input placeholder="Routing number" value={counterparty.bank_routing} onChange={e => setCounterparty({ ...counterparty, bank_routing: e.target.value })} style={inputStyle} />
              </div>
            </div>
          </div>

          {/* Create actions */}
          <div style={{ display: 'flex', gap: 12, alignItems: 'center', padding: '16px 0', borderTop: '2px solid var(--border-subtle)' }}>
            <input placeholder="Contract Number (optional -- auto-generated if blank)" value={contractNumber} onChange={e => setContractNumber(e.target.value)} style={{ ...inputStyle, maxWidth: 360 }} />
            <ActionButton
              label={loading ? 'Creating...' : (!selectedEntity ? 'Select Legal Entity First' : 'Create Contract + All Linked Records')}
              onClick={handleCreate}
              disabled={loading || !selectedEntity}
              variant="success"
            />
            <ActionButton label="Start Over" onClick={reset} variant="secondary" />
          </div>
        </div>
      )}

      {/* Step 3: Created */}
      {step === 'created' && createdResult && (
        <div style={{ textAlign: 'center', padding: '40px 20px' }}>
          <div style={{ fontSize: 48, marginBottom: 12 }}>&#9989;</div>
          <h2 style={{ color: 'var(--positive)', marginBottom: 8 }}>Contract Created Successfully</h2>
          <p style={{ fontSize: 14, color: 'var(--text-secondary)', marginBottom: 24 }}>AI extracted and auto-created all linked records</p>

          <KpiGrid columns={5} items={[
            { label: 'Contract #', value: createdResult.contract_number, color: 'var(--accent)' },
            { label: 'Obligations', value: createdResult.obligations_created, color: 'var(--accent)' },
            { label: 'Price Items', value: createdResult.price_items_created, color: 'var(--accent)' },
            { label: 'Billing Entries', value: createdResult.billing_entries_created, color: 'var(--accent)' },
            { label: 'Risk Score', value: createdResult.risk_score, color: riskColor(createdResult.risk_score) },
          ]} />

          {createdResult.auto_review && (
            <div style={{ padding: '10px 16px', borderRadius: 6, background: 'var(--warning-bg, #fef3c7)', color: 'var(--warning)', fontSize: 12, marginBottom: 20, display: 'inline-block' }}>
              High risk detected -- contract flagged for legal review
            </div>
          )}

          <div style={{ display: 'flex', gap: 12, justifyContent: 'center' }}>
            <ActionButton label="View Contract" onClick={() => navigate(`/contracts/${createdResult.contract_id}`)} />
            <ActionButton label="Upload Another" onClick={reset} variant="secondary" />
          </div>
        </div>
      )}
    </PageLayout>
  );
}

// -- Helper components ----

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div style={{ padding: '14px 16px', background: 'var(--bg-card)', borderRadius: 8, border: '1px solid var(--border-subtle)' }}>
      <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--text-secondary)', marginBottom: 10, textTransform: 'uppercase' as const }}>{title}</div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>{children}</div>
    </div>
  );
}

function Field({ label, value }: { label: string; value?: string | number | null }) {
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12 }}>
      <span style={{ color: 'var(--text-secondary)', textTransform: 'capitalize' as const }}>{label}</span>
      <span style={{ fontWeight: 600, color: 'var(--text-primary)' }}>{value ?? '-'}</span>
    </div>
  );
}

function fileToBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const result = reader.result as string;
      // Strip the data:...;base64, prefix
      const base64 = result.includes(',') ? result.split(',')[1] : result;
      resolve(base64);
    };
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}
